import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, Icon, Label } from 'semantic-ui-react';

import { bem } from '../../core/design/bem';
import { GUESTS_WARNING_THRESHOLD, GroupWithInvited } from '../types';
import InvitedList from './InvitedList';

const css = bem('GroupCard');
const translationPrefix = 'group-visits';

type GroupCardProps = {
  group: GroupWithInvited;
  onEdit: (group: GroupWithInvited) => void;
};

const GroupCard = ({ group, onEdit }: GroupCardProps): JSX.Element => {
  const { t } = useTranslation();
  const {
    name,
    inviterName,
    guide,
    registeredCount,
    totalCount,
    pmrCount,
    parkingCount,
    invited,
  } = group;
  const isOverThreshold = totalCount > GUESTS_WARNING_THRESHOLD;
  const title = name || inviterName || t(`${translationPrefix}.card.untitled`);

  return (
    <Card className={css({ warning: isOverThreshold }).toString()}>
      <Card.Content>
        <Card.Header className={css('header').toString()}>{title}</Card.Header>
        {inviterName && name && (
          <Card.Meta className={css('inviter').toString()}>
            <Icon name="user" />
            {inviterName}
          </Card.Meta>
        )}
        {guide && (
          <Card.Meta className={css('guide').toString()}>
            <Icon name="compass outline" />
            {guide}
          </Card.Meta>
        )}
      </Card.Content>
      <Card.Content className={css('counts').toString()}>
        <Label basic color={isOverThreshold ? 'red' : undefined}>
          <Icon name="users" />
          {t(`${translationPrefix}.card.registered-count`, {
            registered: registeredCount,
            total: totalCount,
          })}
        </Label>
        {pmrCount > 0 && (
          <Label basic>
            <Icon name="wheelchair" />
            {t(`${translationPrefix}.card.pmr-count`, { count: pmrCount })}
          </Label>
        )}
        {parkingCount > 0 && (
          <Label basic>
            <Icon name="car" />
            {t(`${translationPrefix}.card.parking-count`, { count: parkingCount })}
          </Label>
        )}
      </Card.Content>
      {invited.length > 0 && (
        <Card.Content className={css('invited').toString()}>
          <InvitedList invited={invited} />
        </Card.Content>
      )}
      <Card.Content extra className={css('actions').toString()}>
        <Button size="small" basic className={css('edit').toString()} onClick={() => onEdit(group)}>
          <Icon name="pencil" />
          {t('btn.edit')}
        </Button>
      </Card.Content>
    </Card>
  );
};

export default GroupCard;
